/**
 * "Come back here" for pages whose back button normally goes to a fixed list (the product pages go to
 * /products, the looms pages to /looms). A page that sends someone away records its exact address;
 * the pages it names then go back to it and show it in the header trail. Kept for this tab only and
 * for a couple of hours.
 *
 * A trail names its targets two ways: product ids (any page whose second segment is one of them:
 * /product/:id, /edit-product/:id, /view-product/:id) or address prefixes (/looms, /studio/modules).
 */
export type ReturnTrail = { to: string; label: string; productIds: string[]; paths?: string[]; at: number };

const KEY = "hkp.returnTrail";
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

export const setReturnTrail = (to: string, label: string, productIds: string[], paths: string[] = []) => {
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ to, label, productIds: productIds.filter(Boolean), paths: paths.filter(Boolean), at: Date.now() })
    );
  } catch {
    // Storage blocked: back goes to the usual list.
  }
};

export const clearReturnTrail = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing stored.
  }
};

/** The trail for this page (/product/:id, /edit-product/:id, /looms…), if it was opened from one. */
export const returnTrailFor = (pathname?: string): ReturnTrail | null => {
  if (!pathname) return null;
  let t: ReturnTrail | null = null;
  try {
    t = JSON.parse(sessionStorage.getItem(KEY) || "null") as ReturnTrail | null;
  } catch {
    return null;
  }
  if (!t || Date.now() - t.at > MAX_AGE_MS) return null;
  if ((t.paths || []).some((p) => pathname === p || pathname.startsWith(`${p}/`))) return t;
  const id = decodeURIComponent(pathname.split("/")[2] || "");
  return id && (t.productIds || []).includes(id) ? t : null;
};

/**
 * Set in the location state of the page entry a trail sends someone to. While it is there, Back on
 * that page is the browser's Back (the trail's page is the entry right behind), so a second Back
 * keeps walking the history of where they came from instead of bouncing to the product again.
 */
export const RETURN_MARKER = "hkpReturn";

export const hasReturnMarker = (state: unknown): boolean => Boolean((state as any)?.[RETURN_MARKER]);

/** Pages that follow a product trail for as long as it lasts (by product id). */
export const PRODUCT_TRAIL_PAGES = ["product", "view-product", "edit-product", "pre-viewProduct", "pre-ViewProduct-tantuvi"];

/**
 * The trail to show on a page: product pages follow it by product id; other pages (Looms, Studio
 * modules) only on the entry the trail sent them to, so a later visit from the menu shows nothing.
 */
export const shownReturnTrail = (pathname: string | undefined, state: unknown): ReturnTrail | null => {
  if (!pathname) return null;
  if (PRODUCT_TRAIL_PAGES.includes(pathname.split("/")[1] || "")) return returnTrailFor(pathname);
  return hasReturnMarker(state) ? returnTrailFor(pathname) : null;
};
